import { ScreeningQuestion } from '@/types';
import { ScreeningQuestionType } from '@/enums';

/**
 * NUMERIC screening questions store their bounds in `numericResponse`, not
 * `responses`. Strip `responses` for those before sending to the backend —
 * moved out of ProjectCreate.tsx so it can be reused by both the full-save
 * and partial-save (TELV1-490) code paths without duplicating the mapping.
 */
export const sanitizeScreeningQuestionsForPayload = (
  questions: ScreeningQuestion[],
): ScreeningQuestion[] =>
  questions.map((q) => {
    const base = q.type === ScreeningQuestionType.NUMERIC ? { ...q, responses: undefined } : q;
    // Media rides the project save only for label/order edits (files are
    // uploaded through the dedicated media endpoint). Strip the read-time
    // signed `url` — it's not part of the stored shape, and the backend DTO
    // validates screening media strictly (@ValidateNested). `file_id` is
    // stripped for symmetry with the respondent-serving shape (worker adds
    // it alongside url); it never appears in the authoring flow today.
    // TELV1-520.
    if (!Array.isArray(base.media)) return base;
    return {
      ...base,
      media: base.media.map((item) => {
        const {
          url: _url,
          file_id: _fileId,
          ...mediaRest
        } = item as typeof item & {
          file_id?: string;
        };
        return mediaRest;
      }) as typeof base.media,
    };
  });

/**
 * Drops screener answer rows that carry no intent at all: an empty value
 * AND no rule selected. A row with either a value or a rule set is a user
 * in the middle of filling it in — that's a real validation error, not
 * scratch state, so it is left alone and surfaces the normal "please
 * select one of the rules" / "required" error instead of being silently
 * deleted (TELV1-490 architect review, Point 4).
 */
export const pruneNoIntentScreeningAnswers = (
  questions: ScreeningQuestion[] | undefined,
): ScreeningQuestion[] | undefined => {
  if (!questions) return questions;

  let didPrune = false;
  const pruned = questions.map((question) => {
    if (!Array.isArray(question.responses)) return question;

    const keptResponses = question.responses.filter(
      (response) => !(response.value?.trim() === '' && !response.rule),
    );

    if (keptResponses.length === question.responses.length) return question;
    didPrune = true;
    return { ...question, responses: keptResponses };
  });

  return didPrune ? pruned : questions;
};

/**
 * Builds the payload for the deletion-only auto-save that fires when an
 * already-saved screening question is removed (ScreeningQuestions'
 * `handleDeleteQuestion`).
 *
 * It used to be derived from the SERVER list (`screeningQuestions` prop)
 * minus the deleted id. That snapshot can be stale — the prop only catches
 * up when the post-save refetch lands — so deleting two questions in quick
 * succession sent a list that still contained the first one and resurrected
 * it. Deriving from the LIVE editor list instead makes every deletion in
 * this session count, whether or not its refetch has landed yet.
 *
 * Content still comes from the server copy wherever the server has one, so
 * this stays a deletion: half-typed edits to surviving questions are NOT
 * persisted behind the user's back (this path deliberately skips form
 * validation, so those edits may be legitimately invalid — they stay in the
 * editor and go out with the user's next real save).
 *
 * True drafts (never saved) are left out entirely, exactly as before, so
 * blank scratch rows never reach the database. A question that is in
 * NEITHER set — unknown to the server and not a draft — keeps its local
 * copy: dropping it would delete something the user can see. That state is
 * a safety net rather than a common path, because both sets are derived
 * from the same `screeningQuestions` prop (an id stops being a draft
 * exactly when the server list starts carrying it). It shows up when the
 * two desync — e.g. the server list shrinks because another session
 * deleted a question this editor still shows.
 *
 * Note this does NOT cover a deletion fired while a save that CREATED a
 * question is still in flight: the new question is still a draft at that
 * moment, so it is left out and the deletion write can undo the creation.
 * That window predates this helper (the old prop-derived payload dropped it
 * too) and closing it needs the delete affordance to be unavailable while a
 * save is in flight, not a payload change.
 */
export const buildScreeningDeletionPayload = (
  localQuestions: ScreeningQuestion[],
  serverQuestions: ScreeningQuestion[] | undefined,
  draftIds: ReadonlySet<string>,
): ScreeningQuestion[] => {
  const serverById = new Map((serverQuestions ?? []).map((question) => [question.id, question]));

  return localQuestions
    .filter((question) => serverById.has(question.id) || !draftIds.has(question.id))
    .map((question) => serverById.get(question.id) ?? question);
};
